import styled, { keyframes } from 'styled-components'
import ViewAll from '../../ViewAll'
import useTranslation from '../../../../hooks/useTranslation'
import PeriodSelect, { CustomRange } from './PeriodSelect'
import { widgetFont } from '../../widgetTypography'

/*
 * The frame every analytics widget sits in, inside the grid cell GridWidget
 * draws: title and period on top, an optional headline, the chart taking what
 * is left, an optional row of KPIs at the bottom.
 *
 * It is a size container, so its parts restyle themselves by the card's own
 * width rather than the window's — a card two columns wide on a laptop and one
 * column wide on a phone are the same case.
 */

const pulse = keyframes`
    0%, 100% { opacity: 0.45; }
    50% { opacity: 0.8; }
`

export const Card = styled.section`
    container-type: inline-size;
    container-name: analytics;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    height: 100%;
    min-height: 0;
    padding: 0.25rem 0.4rem 0.15rem;
    box-sizing: border-box;
    color: ${p => p.theme.font.color};
`

/*
 * Two rows, two columns: the title with "View all" at its right; under them
 * the subtitle and period on the left, and a custom range's two dates on the
 * right. A card too narrow for both on one line gives the dates a line of their
 * own, under the period.
 */
const Header = styled.header`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
        "title    link"
        "subtitle range";
    align-items: center;
    gap: 0.1rem 0.5rem;
    flex-shrink: 0;

    @container analytics (max-width: 340px) {
        grid-template-areas:
            "title    link"
            "subtitle subtitle"
            "range    range";

        & > [data-area="range"] { justify-self: start; }
        & > [data-area="range"] > * { justify-content: flex-start; }
    }
`

const Title = styled.h3`
    grid-area: title;
    margin: 0;
    ${widgetFont.title}
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Subtitle = styled.span`
    grid-area: subtitle;
    min-width: 0;
    ${widgetFont.subtitle}
    color: ${p => p.theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Period = styled.div`
    grid-area: subtitle;
    min-width: 0;
`

const Range = styled.div`
    grid-area: range;
    justify-self: end;
    min-width: 0;
    max-width: 100%;
`

const HeaderViewAll = styled(ViewAll)`
    grid-area: link;
    justify-self: end;
`

export const Body = styled.div`
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
`

const Skeleton = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    justify-content: flex-end;
`

const SkeletonBar = styled.div`
    height: ${p => p.$h};
    width: ${p => p.$w};
    border-radius: 0.375rem;
    background: ${p => p.theme.table.border};
    animation: ${pulse} 1.4s ease-in-out infinite;
`

const Message = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    text-align: center;
    ${widgetFont.body}
    color: ${p => p.theme.font.label};
`

const Retry = styled.button`
    font: inherit;
    ${widgetFont.label}
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    border: 1px solid ${p => p.theme.table.border};
    background: transparent;
    color: ${p => p.theme.font.color};
    cursor: pointer;

    &:hover { border-color: ${p => p.theme.font.label}; }
    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; }
`

export const LoadingState = () => (
    <Skeleton aria-hidden="true">
        <SkeletonBar $h="1.6rem" $w="40%" />
        <SkeletonBar $h="100%" $w="100%" />
    </Skeleton>
)

/*
 * `status` is what the body shows instead of the content: 'loading', 'error',
 * 'empty', or nothing. Header and footer stay, so the period can still be
 * changed on an empty card — usually how it stops being empty.
 */
const AnalyticsCard = ({ labels = {}, period, path, status, error, onRetry, headline, footer, children }) => {
    const { t } = useTranslation()
    const title = t(labels.title)

    return (
        <Card aria-label={title} aria-busy={status === 'loading'}>
            <Header>
                <Title title={title}>{title}</Title>
                {path && <HeaderViewAll to={path} />}
                {period?.selectable
                    ? <Period><PeriodSelect period={period} subtitle={labels.subtitle ? t(labels.subtitle) : null} /></Period>
                    : labels.subtitle && <Subtitle>{t(labels.subtitle)}</Subtitle>}
                {period?.selectable && period.period === 'custom' && <Range data-area="range"><CustomRange period={period} /></Range>}
            </Header>
            {!status && headline}
            <Body>
                {status === 'loading' && <LoadingState />}
                {status === 'error' && (
                    <Message role="alert">
                        <span>{t('analytics.state.error')}</span>
                        {error?.message && <small>{error.message}</small>}
                        {onRetry && <Retry type="button" onClick={onRetry}>{t('analytics.state.retry')}</Retry>}
                    </Message>
                )}
                {status === 'empty' && <Message>{t('analytics.state.empty')}</Message>}
                {!status && children}
            </Body>
            {!status && footer}
        </Card>
    )
}

export default AnalyticsCard
